import { ArrowRight, ShoppingCart, TicketPercent } from "lucide-react";
import Button from "@/store/components/Button";
import { getActiveCouponForCategory, isFreeForViewer, promoForPurchase } from "@/store/coupons";
import { accessShortcut, isBundleCategory } from "@/store/constants";
import type { Coupon, Product } from "@/store/types";

interface BuyButtonProps {
  product: Product;
  isPurchased: boolean;
  coupons: Coupon[];
  isMember: boolean;
  /** True while any purchase/cart action is in flight (all buttons disable). */
  busy: boolean;
  /** True when THIS product is the one being processed (shows "Processing…"). */
  processing: boolean;
  onPurchase: (product: Product) => void;
  onAddToCart: (product: Product) => void;
}

/**
 * Renders the correct call-to-action for a product:
 *  - Bundle category (GDEXB/IMPEXB) → always "Add to Cart" (repeat purchases allowed)
 *  - Already purchased (non-bundle) → "Access Module" / "Access Study Guide" link
 *  - A usable coupon exists for the category (and it maps to a promotion) and the product
 *    isn't free for this viewer → "Use Coupon"
 *  - Otherwise → "Add to Cart"
 */
export default function BuyButton({
  product,
  isPurchased,
  coupons,
  isMember,
  busy,
  processing,
  onPurchase,
  onAddToCart,
}: BuyButtonProps) {
  const label = processing ? "Processing…" : null;
  const addToCartLabel = (
    <>
      <ShoppingCart size={16} /> Add to Cart
    </>
  );

  // Bundles allow repeat purchases — always Add to Cart, regardless of coupon/purchase state.
  if (isBundleCategory(product.Category)) {
    return (
      <Button variant="primary" size="block" disabled={busy} onClick={() => onAddToCart(product)}>
        {label ?? addToCartLabel}
      </Button>
    );
  }

  // Already-owned non-bundle items link out to the access shortcut. Rendered as a
  // button (not an <a>) so iMIS's host anchor styles can't override the text color,
  // and given the orange accent to set it apart from the blue purchase CTAs.
  if (isPurchased) {
    const { label: accessLabel, href } = accessShortcut(product.Category);
    return (
      <Button
        variant="accent"
        size="block"
        onClick={() => {
          window.location.href = href;
        }}
      >
        {accessLabel} <ArrowRight size={16} />
      </Button>
    );
  }

  const coupon = getActiveCouponForCategory(coupons, product.Category);
  const canUseCoupon =
    !!coupon && !!promoForPurchase(coupon, product.Category) && !isFreeForViewer(product, isMember);

  if (canUseCoupon) {
    return (
      <Button variant="primary" size="block" disabled={busy} onClick={() => onPurchase(product)}>
        {label ?? (
          <>
            <TicketPercent size={16} /> Use Coupon
          </>
        )}
      </Button>
    );
  }

  return (
    <Button variant="primary" size="block" disabled={busy} onClick={() => onAddToCart(product)}>
      {label ?? addToCartLabel}
    </Button>
  );
}
